/**
 * A dialog you can push out of the way.
 *
 * The plan form covers the chart, and the chart is the thing you are writing the
 * plan against — you want to look at the level you are typing while you type it.
 * A modal that cannot move makes you close it, look, and reopen it, which loses
 * whatever was half-entered.
 *
 * ## It drags by its header, not by its body
 *
 * The body is full of inputs. A drag started anywhere would fight text
 * selection in every one of them, and a form you cannot select the contents of
 * is worse than one you cannot move.
 *
 * ## Pointer capture rather than window listeners
 *
 * `setPointerCapture` keeps the drag with the header even when the pointer
 * outruns it — which it will, because a drag that starts on a 20px strip and
 * moves fast leaves that strip within a frame. Window listeners get the same
 * result until the pointer crosses an iframe or leaves the document, where
 * capture keeps working and they stop.
 *
 * ## It stays on screen
 *
 * Clamped to the viewport on every move, because a dialog dragged off the top
 * takes its own header with it and there is then nothing left to drag back.
 *
 * ## It carries the theme
 *
 * The `--qf-*` variables live on the shell root, and a dialog portalled to the
 * body (☰ › AI usage) is outside it: every `var(--qf-surface)` was undefined and
 * the dialog was see-through over the chart. So it sets them itself, as the
 * indicator picker's sheet and its Move to menu do.
 */

import { useMemo, useRef, useState, type ReactNode } from "react";
import { themeCssVariables } from "@qufolio/model";

import { useTheme } from "./theme";

export interface DraggableDialogProps {
  /** Rendered in the drag handle, left-aligned. */
  title: ReactNode;
  /** Closes the dialog. Rendered as the × at the handle's right. */
  onClose: () => void;
  children: ReactNode;
}

export function DraggableDialog({ title, onClose, children }: DraggableDialogProps) {
  // `null` means "not placed yet", which centres it. Once dragged it holds a
  // real position — so opening the form always starts centred, and moving it is
  // a deliberate act rather than something it remembers wrongly.
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const grab = useRef<{ dx: number; dy: number } | null>(null);

  const onPointerDown = (event: React.PointerEvent) => {
    // **A press on a control in the handle is not a drag, and treating it as one
    // swallowed the control.** `setPointerCapture` retargets the rest of the
    // gesture to the element that captured — including the `click`, which is a
    // PointerEvent — so the close button was pressed, highlighted, and never
    // heard from: its `onClick` fired on the handle instead. The symptom is a ×
    // that does nothing, with no error anywhere.
    //
    // Checked against `target` rather than `currentTarget`, which is always the
    // handle, and by `closest` so a glyph inside a button counts as the button.
    if ((event.target as Element | null)?.closest?.("button, a, input, select, textarea")) {
      return;
    }
    const box = hostRef.current?.getBoundingClientRect();
    if (!box) return;
    // The offset within the handle, so the dialog does not jump to centre
    // itself on the cursor the moment a drag begins.
    grab.current = { dx: event.clientX - box.left, dy: event.clientY - box.top };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const onPointerMove = (event: React.PointerEvent) => {
    const held = grab.current;
    const box = hostRef.current?.getBoundingClientRect();
    if (!held || !box) return;
    // Clamped so the handle can never leave the viewport — drag it off the top
    // and the only thing that could bring it back has gone with it.
    const maxX = Math.max(0, window.innerWidth - box.width);
    const maxY = Math.max(0, window.innerHeight - box.height);
    setAt({
      x: Math.min(Math.max(0, event.clientX - held.dx), maxX),
      y: Math.min(Math.max(0, event.clientY - held.dy), maxY),
    });
  };

  const theme = useTheme();
  const themeVars = useMemo(() => themeCssVariables(theme) as React.CSSProperties, [theme]);

  const endDrag = (event: React.PointerEvent) => {
    grab.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <div
      ref={hostRef}
      style={
        at
          ? { ...themeVars, ...HOST, left: at.x, top: at.y }
          : // Centred until it is moved. `transform` rather than computed
            // coordinates, because the dialog's size is its content's and is not
            // known before it renders.
            { ...themeVars, ...HOST, left: "50%", top: "50%", transform: "translate(-50%, -50%)" }
      }
    >
      <div
        style={HANDLE}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <span style={{ fontSize: 12, fontWeight: 600 }}>{title}</span>
        <button type="button" onClick={onClose} aria-label="Close" style={CLOSE}>
          ×
        </button>
      </div>
      {children}
    </div>
  );
}

const HOST: React.CSSProperties = {
  position: "fixed",
  zIndex: 60,
  display: "flex",
  flexDirection: "column",
  maxHeight: "90vh",
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
  overflow: "hidden",
};

const HANDLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "5px 8px",
  background: "var(--qf-surface-alt)",
  borderBottom: "1px solid var(--qf-border)",
  color: "var(--qf-text)",
  cursor: "move",
  // The handle is a grip, not a caption: a drag that selected the title text
  // would look like a failed drag.
  userSelect: "none",
  touchAction: "none",
  flex: "0 0 auto",
};

const CLOSE: React.CSSProperties = {
  width: 20,
  height: 20,
  padding: 0,
  background: "transparent",
  border: "none",
  borderRadius: 4,
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
  font: "inherit",
  fontSize: 15,
  lineHeight: 1,
};
